/* eslint-disable lingui/no-unlocalized-strings -- chaves e valores técnicos do fio (U02); o texto de UI sai por msg */

import { civilDateAt, parseInstant } from '@/shared/format/date';
import { creditApi } from '@/shared/http';

import { isRecord, lower, str, type Values } from './credit-note.form-helpers';
import type { CreditNote, CreditProduct } from './credit-note.wire';
import type { CreditNoteTabsValues } from './screens/tab-form';

// A parte leve do formulário da operação (CreditNoteFormContainer.tsx): os tipos, os valores do `GET`, as datas do
// 1º pagamento, a simulação recuperada, a cópia do "Duplicar como novo" e o fio (services/creditNote/creditNote.ts,
// services/Amortization/*). Entra no chunk do detalhe. As regras pesadas (validação do yup, cast e corpo do legado,
// padrões e troca de produto) ficam em `credit-note.form.ts`, que o detalhe carrega por `import()` no Salvar e no
// Simular (U28); o schema do RHF as carrega no primeiro uso.



/** A amortização do formulário: o `AmortizationReadModel` do `GET` ou os padrões do tipo, com os campos editados. */
export type AmortizationValues = Values & { amortizationType?: string | null | undefined; requestedAmount?: number | null | undefined };

/** Os valores do formulário da operação. Tem os campos do contrato das abas (`screens/tab-form.tsx`). */
export interface CreditNoteFormValues extends CreditNoteTabsValues {
  [key: string]: unknown;
  amortization: AmortizationValues;
}

/** O produto do formulário: o `GET /Product/{id}` (os campos que o formulário lê passam pelo `looseObject`). */
export type FormProduct = CreditProduct & Values;

export type FormConfig = Readonly<Record<string, unknown>>;


// ─── datas no formato do legado ─────────────────────────────────────────────

/** `toIsoStringWithTimezone` da meia-noite de um dia civil (DateFormat.ts:218-235): o que o `DatePickerFormField` grava. */
export function civilToLegacyIso(civil: string): string {
  return `${civil}T00:00:00.000-03:00`;
}

/** O dia civil que o seletor mostra para um valor do formulário (instante lido em São Paulo). */
export function legacyIsoToCivil(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  const instant = parseInstant(value);
  return instant ? civilDateAt(instant) : null;
}

/** Hoje em São Paulo, como dia civil. */
export function todayCivilAt(now: Date): string {
  return civilDateAt(now);
}

function addCivil(civil: string, amount: number, unit: 'days' | 'months' | 'years'): string {
  const [y = 0, m = 1, d = 1] = civil.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (unit === 'days') date.setUTCDate(date.getUTCDate() + amount);
  else if (unit === 'months') {
    const target = new Date(Date.UTC(y, m - 1 + amount, 1));
    const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, last));
    return target.toISOString().slice(0, 10);
  } else date.setUTCFullYear(date.getUTCFullYear() + amount);
  return date.toISOString().slice(0, 10);
}

/** `new Date(ano, mês, dia)` local (mês pode estourar, como no `Date` do JS), meia-noite de São Paulo em ISO UTC. */
function localDateIso(year: number, monthIndex: number, day: number, hour = 0): string {
  const utc = new Date(Date.UTC(year, monthIndex, day));
  const civil = utc.toISOString().slice(0, 10);
  return new Date(`${civil}T${String(hour).padStart(2, '0')}:00:00-03:00`).toISOString();
}

/** O tipo de amortização que as abas leem (`product?.amortizationType?.toLowerCase() ?? 'cleanprice'`, OperationTab.tsx:127). */
export function productAmortizationType(product: FormProduct | undefined, values?: Values): string {
  const fromProduct = lower(product?.amortizationType);
  if (fromProduct) return fromProduct;
  const amortization = values?.['amortization'];
  return lower(isRecord(amortization) ? amortization['amortizationType'] : undefined) || 'cleanprice';
}

/** A chave do schema e dos padrões (`(product?.amortizationType || 'default').toLowerCase()`, CreditNoteSchema.ts:366). */
export function schemaKind(product: FormProduct | undefined): string {
  return lower(product?.amortizationType) || 'default';
}

/** O `GET /CreditNote/{id}` como valores do formulário (`defaultValues = creditData`), com os campos das abas. */
export function noteToFormValues(note: CreditNote): CreditNoteFormValues {
  const raw = structuredClone(note) as Values;
  const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
  const values: CreditNoteFormValues = {
    ...raw,
    warranty: list<CreditNoteFormValues['warranty'][number]>(raw['warranty']),
    collateral: (raw['collateral'] as string | null | undefined) ?? null,
    relatedPersonCreditNote: list<CreditNoteFormValues['relatedPersonCreditNote'][number]>(raw['relatedPersonCreditNote']),
    uploads: list<CreditNoteFormValues['uploads'][number]>(raw['uploads']),
    amortization: isRecord(raw['amortization']) ? raw['amortization'] : {},
  };
  return values;
}

/** `setFirstPaymentDayToProductWithPayrollAgreement` (setFirstPaymentDate.ts:123-176): meio-dia local em ISO. */
export function firstPaymentDateForPayrollAgreement(startDate: string, agreement: Values, product: FormProduct | undefined, config: FormConfig): string | null {
  const start = parseInstant(startDate);
  if (!start) return null;
  const [y = 0, m = 1, d = 1] = civilDateAt(start).split('-').map(Number);
  const office = product?.warrantyRegistrationOffice ?? agreement['warrantyRegistrationOffice'];
  const transferDay = (agreement['paymentTransferDay'] as number | null | undefined) ?? (config['SET_FIRST_PAYMENT_ON_DAY'] as number | undefined) ?? 0;
  const months = (agreement['monthsUntilFirstPayment'] as number | null | undefined) ?? 2;
  const cutoff = agreement['warrantyRegistrationCutoffDay'] as number | null | undefined;
  const at = (additional: number) => localDateIso(y, m - 1 + additional, transferDay, 12);
  if (cutoff && product?.categoryName === 'CARTÃO BENEFÍCIO') return at(d >= cutoff ? months : months - 1);
  if (cutoff && cutoff === d && office === 'Others') return at(1);
  if (cutoff && office === 'Siape') return at(d > cutoff ? months + 1 : months);
  return at(months);
}

const PERIODICITY_UNIT: Readonly<Record<string, 'days' | 'months' | 'years'>> = { Daily: 'days', Monthly: 'months', Yearly: 'years' };

/**
 * `handleFirstPaymentDateDelegate` sem o Dataprev (Helper/handleFirstPaymentDateDelegate.ts:26-89): ao mudar a
 * data de início. Devolve a nova data do primeiro pagamento ou `undefined` quando o legado não mexe.
 */
export function firstPaymentDateOnStart(values: CreditNoteFormValues, product: FormProduct | undefined, config: FormConfig): string | undefined {
  const amortization = values.amortization;
  const startDate = str(amortization['startDate']);
  const agreement = product?.['payrollAgreement'];
  if (product && isRecord(agreement)) return firstPaymentDateForPayrollAgreement(startDate, agreement, product, config) ?? undefined;
  const office = lower(product?.warrantyRegistrationOffice);
  const isSiape = office === 'siape' && lower(product?.instrumentType) === 'publicpayrollloan';
  const autoDay = config['SET_FIRST_PAYMENT_ON_DAY'];
  const validDay = typeof autoDay === 'number' && autoDay >= 1 && autoDay <= 31;
  const start = parseInstant(startDate);
  if (startDate && !isSiape) {
    // calculateAndSetFirstPaymentDate (setFirstPaymentDate.ts:24-55).
    if (validDay && start) {
      const [y = 0, m = 1] = civilDateAt(start).split('-').map(Number);
      return localDateIso(y, m - 1 + 2, autoDay);
    }
    const periodicity = isRecord(amortization['paymentPeriodicity']) ? amortization['paymentPeriodicity'] : {};
    const every = Number(periodicity['every']);
    // `executeAmortizationBasedOnType` sai do produto (CreditNoteFormContainer.tsx:204-205).
    const type = lower(product?.amortizationType) || 'cleanprice';
    if (every && ['price', 'sac', 'cleanprice'].includes(type) && start) {
      const unit = PERIODICITY_UNIT[str(periodicity['periodicity'])] ?? 'days';
      return new Date(`${addCivil(civilDateAt(start), every, unit)}T00:00:00-03:00`).toISOString();
    }
    return undefined;
  }
  // handlePaymentAndCutoffDays(start, PAYMENTDAY_FIELD, SET_FIRST_PAYMENT_ON_DAY) (:81-87).
  const transferDay = Number(config['PAYMENTDAY_FIELD']);
  const cutoffDay = Number(autoDay);
  if (!startDate || !transferDay || !cutoffDay) return undefined;
  return paymentTransferAndCutoffDate(startDate, transferDay, cutoffDay);
}

/** `setPaymentTransferAndCutoffDay` (setFirstPaymentDate.ts:97-121). */
export function paymentTransferAndCutoffDate(startDate: string, transferDay: number, cutoffDay: number): string | undefined {
  const start = parseInstant(startDate);
  if (!start) return undefined;
  const [y = 0, m = 1, d = 1] = civilDateAt(start).split('-').map(Number);
  if (cutoffDay > d) return transferDay ? localDateIso(y, m - 1 + 1, transferDay) : undefined;
  return localDateIso(y, m - 1 + 2, transferDay);
}

/**
 * OperationTab.tsx:245-259: no consignado público fora do Dataprev, o convênio da 1ª garantia (pelo código do órgão,
 * `getPayrollLoanDetails` em setFirstPaymentDate.ts:76-95) fixa a 1ª data pelo repasse e pelo corte. A aba e o
 * delegate da data de início (o mesmo do `save` do agente) passam por aqui. `undefined` quando a regra não vale.
 */
export function publicPayrollFirstPaymentDate(values: CreditNoteFormValues, product: FormProduct | undefined): string | undefined {
  if (lower(product?.instrumentType) !== 'publicpayrollloan' || lower(product?.warrantyRegistrationOffice) === 'dataprevcreditotrabalhador') return undefined;
  const agreements = product?.['payrollAgreements'];
  if (!Array.isArray(agreements) || agreements.length === 0) return undefined;
  const first = values.warranty[0] as Values | undefined;
  const raw = first?.['codOrgao'];
  const code = (typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '0').replace(/\D/g, '');
  const agreement = (agreements as unknown[]).find((item): item is Values => isRecord(item) && item['internalId'] === code);
  const transfer = Number(agreement?.['paymentTransferDay']);
  const cutoff = Number(agreement?.['warrantyRegistrationCutoffDay']);
  const startDate = values.amortization['startDate'];
  if (!transfer || !cutoff || typeof startDate !== 'string') return undefined;
  return paymentTransferAndCutoffDate(startDate, transfer, cutoff);
}

export type SaveMode = 'create' | 'edit';

/** `IS_REQUIRED_WARRANTY_CREDIT_NOTE` na criação (CreditNoteFormContainer.tsx:143-154). */
export function missingRequiredWarranty(values: CreditNoteFormValues, product: FormProduct | undefined, config: FormConfig, mode: SaveMode): boolean {
  return mode === 'create' && product?.useCollateral === true && Boolean(config['IS_REQUIRED_WARRANTY_CREDIT_NOTE']) && values.warranty.length === 0;
}

/** `isDateInThePast(new Date(amortization.startDate))`: compara dias (IsDateInThePast.ts:8-17). */
export function isStartInPast(values: CreditNoteFormValues, now: Date): boolean {
  const start = parseInstant(str(values.amortization['startDate']));
  if (!start) return false;
  return civilDateAt(start) < todayCivilAt(now);
}

/** "Atualizar e simular": `new Date(new Date().setHours(0, 0, 0, 0)).toISOString()` (creditNoteActions.tsx:535-540). */
export function todayStartIso(now: Date): string {
  return new Date(`${todayCivilAt(now)}T00:00:00-03:00`).toISOString();
}

/** A janela de manutenção do Crédito do Trabalhador (GenericMethods.ts:351-375): dia 20, de 20:01 às 23:59. */
export function inWorkerCreditWindow(product: FormProduct | undefined, now: Date): boolean {
  if (lower(product?.warrantyRegistrationOffice) !== 'dataprevcreditotrabalhador') return false;
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(now);
  const part = (type: string) => Number(parts.find((item) => item.type === type)?.value ?? '0');
  const hour = part('hour');
  return part('day') === 20 && (hour > 20 || (hour === 20 && part('minute') >= 1));
}

/** O que a aba Operação mostra: a simulação feita agora ou a amortização gravada (OperationTabContainer.tsx:15-19). */
export function operationResult(values: CreditNoteFormValues, note: CreditNote | undefined): Values {
  const simulation = values['createSimulation'];
  if (isRecord(simulation)) return simulation;
  return { ...(note?.amortization ?? {}), paymentScheduleItems: note?.paymentScheduleItems ?? [] };
}

// ─── Recuperar simulação (getRecoverSimulation, RecoverSimulationForm.tsx:29-77) ───

/**
 * O que o legado grava no formulário: cada chave de `amortization` (com o `requestedAmount` zerado trocado pelo
 * `initialValue`, ValiadationRequestedAmount.ts:8-13), produto, tomador, garantias e `createSimulation`.
 */
/**
 * Marca do "Recuperar simulação" pelo diálogo, que grava `warranty: undefined` (RecoverSimulationForm.tsx:62, sem o
 * `warranty` do `?simulacao=`): a lista da tela fica vazia e o Salvar omite a chave enquanto ninguém incluir garantia.
 */
export const RECOVERED_WITHOUT_WARRANTY = '__recoveredWithoutWarranty';

/**
 * Marca da simulação recuperada pela Nova (`?simulacao=`), que grava `warranty` mesmo vazia (CreditNoteForm.tsx:43,
 * RecoverSimulationForm.tsx:64): a chave existe no formulário do legado e o Salvar da criação manda `warranty: []`.
 */
export const RECOVERED_WITH_WARRANTY = '__recoveredWithWarranty';

/** Tira a marca e, com a lista ainda vazia, a chave `warranty` (o `undefined` do legado não vai no JSON). */
export function dropRecoveryMarker(values: Values): Values {
  const out = { ...values };
  const empty = out[RECOVERED_WITHOUT_WARRANTY] === true && Array.isArray(out['warranty']) && out['warranty'].length === 0;
  Reflect.deleteProperty(out, RECOVERED_WITHOUT_WARRANTY);
  Reflect.deleteProperty(out, RECOVERED_WITH_WARRANTY);
  if (empty) Reflect.deleteProperty(out, 'warranty');
  return out;
}

export function recoveredSimulation(current: CreditNoteFormValues, simulation: Values, warranty?: readonly Values[]): CreditNoteFormValues {
  const source = isRecord(simulation['amortization']) ? simulation['amortization'] : {};
  const amortization: AmortizationValues = { ...current.amortization };
  for (const [key, value] of Object.entries(source)) {
    amortization[key] = key === 'requestedAmount' && (value === undefined || value === null || value === 0) ? source['initialValue'] : value;
  }
  return {
    ...current,
    amortization,
    productId: simulation['productId'],
    productIdDisplay: simulation['productIdDisplay'],
    personDiscriminator: simulation['personDiscriminator'],
    personId: simulation['personId'],
    personDisplay: simulation['personDisplay'],
    warranty: (warranty ?? []) as CreditNoteFormValues['warranty'],
    [RECOVERED_WITHOUT_WARRANTY]: warranty === undefined ? true : undefined,
    [RECOVERED_WITH_WARRANTY]: warranty === undefined ? undefined : true,
    createSimulation: { ...source, paymentScheduleItems: simulation['paymentScheduleItems'] },
  };
}

/** Duplicar como novo: o `GET` sem os dados do registro, em Rascunho, para abrir na Nova (`redirectToStateNewCreditNote`). */
export function cloneAsNewValues(note: CreditNote): CreditNoteFormValues {
  const cloned = noteToFormValues(note) as Values;
  for (const key of ['id', 'number', 'createdAt', 'updatedAt', 'updatedBy', 'createdBy', 'statusDisplay', 'createSimulation']) Reflect.deleteProperty(cloned, key);
  cloned['timeline'] = [];
  cloned['linkedTo'] = undefined;
  cloned['liquidationSchedule'] = null;
  cloned['status'] = 'Draft';
  return cloned as CreditNoteFormValues;
}

// ─── fio ────────────────────────────────────────────────────────────────────

/**
 * `POST /CreditNote?tenant` (creditNote.ts:256-262): a resposta é o id da operação nova (texto ou `{ id }`).
 * Sem id devolve `undefined`: quem chama trata como erro (nunca navega para `/CreditNote/undefined`).
 */
export async function createCreditNote(body: Values): Promise<string | undefined> {
  const data = await creditApi.post('/CreditNote', body, { withTenant: true });
  if (typeof data === 'string') return data === '' ? undefined : data;
  return isRecord(data) && typeof data['id'] === 'string' && data['id'] !== '' ? data['id'] : undefined;
}

/** `PUT /CreditNote/{id}?tenant` (creditNote.ts:256-262). */
export async function saveCreditNote(id: string, body: Values): Promise<unknown> {
  return creditApi.put<unknown>(`/CreditNote/${id}`, body, { withTenant: true });
}

/** `createSimulationCreditNote` (Amortization.tsx:7-23): `POST /Amortization`, sem `tenant`. */
export async function simulateAmortization(body: Values): Promise<Values> {
  const data = await creditApi.post('/Amortization', body);
  return isRecord(data) ? data : {};
}

/** `postFirstPaymentDateAsync` (DataprevEmployee.ts:74-78): `POST /DataprevEmployee/FirstPaymentDate?productId&startDate`, corpo `{}`. */
export async function firstPaymentDateDataprev(productId: string, startDate: string, signal?: AbortSignal): Promise<string | null> {
  const data = await creditApi.post('/DataprevEmployee/FirstPaymentDate', {}, { params: { productId, startDate }, ...(signal ? { signal } : {}) });
  return isRecord(data) && typeof data['firstPaymentDate'] === 'string' ? data['firstPaymentDate'] : null;
}

/** `getLastIssueNumber` (creditNote.ts:779-791): a próxima emissão e série da Nota Comercial. */
export async function lastIssueNumber(productId: string, fundId: string, personId: string, signal?: AbortSignal): Promise<{ nextEmissionNumber?: number; nextIssueNumber?: number }> {
  const data = await creditApi.get<unknown>('/CreditNote/GetLastIssueNumber', { params: { productId, fundId, personId }, ...(signal ? { signal } : {}) });
  return isRecord(data) ? data : {};
}

/** As contas do tomador ou do beneficiário (`useRelatedBankAccountData`, relatedBankAccountContext.tsx:30-110): o cadastro da pessoa. */
export async function personBankAccounts(recordType: 'NaturalPerson' | 'LegalPerson', id: string, signal?: AbortSignal): Promise<Values[]> {
  const data = await creditApi.get<unknown>(`/${recordType}/${id}`, signal ? { signal } : {});
  const accounts = isRecord(data) ? data['bankAccounts'] : undefined;
  return Array.isArray(accounts) ? accounts.filter(isRecord) : [];
}

// ─── schema do RHF ──────────────────────────────────────────────────────────

